import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, MapPin, Tag } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ListingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setListing(null);
    axios
      .get(`${API}/listings/${id}`)
      .then((r) => {
        setListing(r.data);
        return axios.get(`${API}/listings`, { params: { city: r.data.city } });
      })
      .then((r) => setRelated(r.data.filter((l) => l.id !== id).slice(0, 3)))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Nav solid />
        <div className="pt-40 pb-32 text-center px-6">
          <h1 className="font-serif-display text-5xl">Listing not found</h1>
          <Link to="/#directory" className="mt-6 inline-block font-accent uppercase text-xs tracking-[0.25em] border border-black px-7 py-3.5 hover:bg-black hover:text-white transition-colors">
            Back to Directory
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!listing) {
    return <div className="min-h-screen bg-white" />;
  }

  return (
    <div className="bg-white text-[#0a0a0a]" data-testid="listing-detail">
      <Nav solid />
      <section className="relative h-[60vh] md:h-[72vh] bg-black overflow-hidden grain">
        <img src={listing.image} alt={listing.name} className="absolute inset-0 h-full w-full object-cover opacity-85" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />
        <div className="relative h-full flex flex-col justify-end px-4 md:px-8 lg:px-12 pb-12 text-white">
          <button
            onClick={() => navigate(-1)}
            data-testid="listing-back"
            className="mb-8 flex items-center gap-2 font-accent uppercase text-xs tracking-[0.25em] text-white/70 hover:text-white transition-colors w-fit"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-4 mb-3 font-accent uppercase text-xs tracking-[0.25em] text-white/70">
            <span className="flex items-center gap-1.5"><MapPin size={13} /> {listing.city}</span>
            <span className="h-3 w-px bg-white/40" />
            <span className="flex items-center gap-1.5"><Tag size={13} /> {listing.category}</span>
            {listing.featured && (
              <span className="bg-white text-black px-2.5 py-1 text-[9px]">Featured</span>
            )}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif-display text-5xl md:text-8xl leading-[0.9] tracking-tight"
          >
            {listing.name}
          </motion.h1>
        </div>
      </section>

      <section className="px-4 md:px-8 lg:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <span className="font-accent uppercase text-xs tracking-[0.3em] text-[#4a4a4a]">About</span>
          <p className="mt-5 font-serif-display text-3xl md:text-4xl leading-snug">{listing.blurb}</p>
        </div>
        <div className="md:col-span-5 md:border-l md:border-black/10 md:pl-12">
          <dl className="space-y-6 font-light">
            <div>
              <dt className="font-accent uppercase text-[10px] tracking-[0.25em] text-[#4a4a4a]">City</dt>
              <dd className="text-xl mt-1">{listing.city}</dd>
            </div>
            <div>
              <dt className="font-accent uppercase text-[10px] tracking-[0.25em] text-[#4a4a4a]">Category</dt>
              <dd className="text-xl mt-1">{listing.category}</dd>
            </div>
            <div>
              <dt className="font-accent uppercase text-[10px] tracking-[0.25em] text-[#4a4a4a]">Placement</dt>
              <dd className="text-xl mt-1">{listing.tier}</dd>
            </div>
          </dl>
          {listing.website && (
            <a
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="listing-website"
              className="mt-8 inline-flex items-center gap-2 font-accent uppercase text-xs tracking-[0.25em] bg-black text-white px-7 py-3.5 hover:bg-[#333] transition-colors"
            >
              Visit Website <ArrowUpRight size={16} />
            </a>
          )}
        </div>
      </section>

      {related.length > 0 && (
        <section className="px-4 md:px-8 lg:px-12 pb-24 md:pb-32">
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight mb-10">
            More in <span className="italic font-light">{listing.city}</span>
          </h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((l) => (
              <Link
                key={l.id}
                to={`/listing/${l.id}`}
                data-testid={`related-listing-${l.id}`}
                className="group block border border-black/10 hover:border-black/40 transition-colors"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={l.image} alt={l.name} className="h-full w-full object-cover transition-transform duration-[1.1s] group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <span className="font-accent uppercase text-[10px] tracking-[0.22em] text-[#4a4a4a]">{l.category}</span>
                  <h3 className="font-serif-display text-2xl mt-1">{l.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
      <Footer />
    </div>
  );
};

export default ListingDetail;
